
import React, { useState, useEffect, memo } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon } from './LandingIcons';

export interface SmilePair {
    before: string;
    after: string;
}

const ImageDetailModal: React.FC<{ pair: SmilePair; onClose: () => void }> = ({ pair, onClose }) => {
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    return (
        <div className="fixed inset-0 bg-zinc-900/95 backdrop-blur-xl z-[100] flex items-center justify-center p-4 animate-fade-in" onClick={onClose} role="dialog" aria-modal="true" aria-label="Smile Transformation Detail">
            <div className="relative w-full max-w-6xl max-h-[90vh] overflow-y-auto bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-800 flex flex-col items-center p-2" onClick={(e) => e.stopPropagation()}>

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-50 text-white bg-black/60 rounded-full p-2.5 hover:bg-black/80 transition-all backdrop-blur-md hover:scale-110 border border-white/10"
                    aria-label="Close modal"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* Side-by-Side View */}
                <div className="flex flex-col md:flex-row w-full h-full gap-2 md:gap-4 p-2 md:p-4">

                    {/* Before Image */}
                    <div className="relative flex-1 rounded-2xl overflow-hidden border border-zinc-800 shadow-lg group">
                        <div className="absolute top-4 left-4 z-10 bg-black/70 backdrop-blur-md text-white text-sm font-semibold px-4 py-1.5 rounded-full border border-white/10 shadow-lg">
                            Before
                        </div>
                        <img
                            src={pair.before}
                            alt="Before transformation"
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            loading="lazy"
                        />
                    </div>

                    {/* After Image */}
                    <div className="relative flex-1 rounded-2xl overflow-hidden border border-teal-500/30 shadow-[0_0_30px_rgba(20,184,166,0.15)] group">
                        <div className="absolute top-4 left-4 z-10 bg-teal-500 text-white text-sm font-semibold px-4 py-1.5 rounded-full shadow-lg">
                            After
                        </div>
                        <img
                            src={pair.after}
                            alt="After transformation"
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            loading="lazy"
                        />
                        {/* Subtle Glow Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-teal-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                    </div>

                </div>

                <div className="pb-4 text-center">
                    <p className="text-zinc-500 text-sm">Tap anywhere outside to close</p>
                </div>
            </div>
        </div>
    );
};

const SmilePairImage = memo(({ pair, onClick }: { pair: SmilePair; onClick: () => void }) => (
    <motion.button
        onClick={onClick}
        whileHover={{ scale: 1.02 }}
        whileFocus={{ scale: 1.02 }}
        className="group relative w-full aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl border border-zinc-200 dark:border-zinc-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-teal-500"
        aria-label="View smile transformation"
    >
        <div className="absolute inset-0 bg-zinc-900" />
        <img src={pair.after} alt="Smile transformation after" className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity duration-500" loading="lazy" />
        <img src={pair.before} alt="Smile transformation before" className="absolute inset-0 w-full h-full object-cover opacity-100 group-hover:opacity-0 group-focus:opacity-0 transition-opacity duration-500" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity duration-300" />
        <div className="absolute bottom-4 left-4 right-4 translate-y-4 group-hover:translate-y-0 group-focus:translate-y-0 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-all duration-300">
            <span className="inline-flex items-center gap-2 text-white text-sm font-medium">
                <SparklesIcon className="w-4 h-4 text-teal-400" />
                View Transformation
            </span>
        </div>
    </motion.button>
));

export const ImageColumn = memo(({ pairs, speed, onImageClick }: { pairs: SmilePair[]; speed: number; onImageClick: (pair: SmilePair) => void }) => {
    return (
        <div className="flex flex-col gap-6" style={{ animation: `scroll-vertical-${speed === 1 ? 'fast' : speed === 2 ? 'medium' : 'slow'} ${speed * 20}s linear infinite` }}>
            {[...pairs, ...pairs].map((pair, i) => (
                <SmilePairImage key={`${i}-${pair.before}`} pair={pair} onClick={() => onImageClick(pair)} />
            ))}
        </div>
    );
});

export default ImageDetailModal;
